import { chain, router as nativeRouter } from '@leprecoins/effector';
import { SOCKET_STATUSES } from '@leprecoins/effector-sockets';
import { combine, createStore, sample } from 'effector';
import { equals } from 'patronum';
import {
	AuthDto,
	authModel,
	createGetAccountQuery,
	createGetCashdeskQuery,
	logoutMutation,
	socket,
	USER_ROLES,
} from 'shared/api';
import { createModalSwitcher } from 'shared/lib';
import { router } from 'shared/routing';

const switcher = createModalSwitcher();

const getAccountQuery = createGetAccountQuery();

const getCashdeskQuery = createGetCashdeskQuery();

const $profile = createStore<AuthDto | null>(null);

const $role = $profile.map((profile) => profile?.role ?? null);

const $id = $profile.map((profile) => profile?.id ?? null);

const $name = $profile.map((profile) => profile?.name ?? null);

const $cashdesk = getCashdeskQuery.$data;

const $shortName = $name.map(
	(name) =>
		name
			?.trim()
			.replace(/\s+/g, ' ')
			.split(' ')
			.slice(0, 2)
			.map((s) => s.at(0)?.toUpperCase())
			.join('') ?? null,
);

const $accountId = $profile.map((profile) => profile?.accountId ?? null);

const $account = getAccountQuery.$data;

const $canCreateSubAccounts = $account.map(
	(account) => account?.permissions.canCreateSubAccounts ?? false,
);

const $canWriteUsers = $account.map((account) => account?.permissions.canWriteUsers ?? false);

const $canEditLimits = $account.map((account) => account?.permissions.canEditLimits ?? false);

const $canEditGameSettings = $account.map(
	(account) => account?.permissions.canEditGameSettings ?? false,
);

const $canWriteCashdesks = $account.map(
	(account) => account?.permissions.canWriteCashdesks ?? false,
);

const $canBlockGames = $account.map((account) => account?.permissions.canBlockGames ?? false);

const $permissions = combine({
	canCreateSubAccounts: $canCreateSubAccounts,
	canWriteUsers: $canWriteUsers,
	canEditLimits: $canEditLimits,
	canEditGameSettings: $canEditGameSettings,
	canWriteCashdesks: $canWriteCashdesks,
	canBlockGames: $canBlockGames,
});

const logout = logoutMutation.start;

chain(authModel.authorized, $profile);

sample({
	clock: authModel.authorized,
	fn: ({ accountId }) => accountId,
	target: getAccountQuery.start,
});

sample({
	clock: authModel.authorized,
	filter: ({ cashdeskId }) => cashdeskId !== null,
	fn: ({ cashdeskId }) => cashdeskId!,
	target: getCashdeskQuery.start,
});

sample({
	clock: authModel.authorized,
	filter: ({ role }) => role === USER_ROLES.cashier,
	target: socket.connect,
});

sample({
	clock: authModel.unauthorized,
	filter: equals(socket.$status, SOCKET_STATUSES.connected),
	target: socket.disconnect,
});

chain(router.$path, switcher.disable);

chain(logoutMutation.finished.success, nativeRouter.reload);

export const viewerModel = {
	switcher,
	getAccountQuery,
	getCashdeskQuery,
	$profile,
	$id,
	$name,
	$shortName,
	$accountId,
	$account,
	$canCreateSubAccounts,
	$canWriteUsers,
	$canEditLimits,
	$canEditGameSettings,
	$canWriteCashdesks,
	$canBlockGames,
	$permissions,
	$cashdesk,
	$role,
	logout,
};
